import { useEffect, useState } from "react";

import { Alert, Autocomplete, DialogContent, Grid, TextField, Typography } from "@mui/material";
import FilterBar from "common/components/FilterBar/FilterBar";
import GenericAlert from "common/components/GenericAlert/GenericAlert";
import GenericDialog from "common/components/GenericDialog/GenericDialog";
import SelectProvinceAndLocation from "common/components/SelectProvinceAndLocation/SelectProvinceAndLocation";
import { ApiService } from "common/services/ApiService";
import { useAuth } from "context/AuthContext";
import DialogEstablishmentListComponent from "modules/authentication/pages/SuperAdmin/components/DialogEstablishmentListComponent";
import { DialogResetPasswordContent } from "modules/authentication/pages/SuperAdmin/components/DialogResetPasswordContent";
import DialogUserContent from "modules/authentication/pages/SuperAdmin/components/DialogUserContent";
import DialogUserEstablishmentContent from "../../pages/SuperAdmin/components/DialogUserEstablishmentContent";
import DialogUserRoleContent from "../../pages/SuperAdmin/components/DialogUserRoleContent";
import { getUsersRadioGroupItems } from "../../pages/SuperAdmin/utils/constants";
import getUsersColumns from "./getUsersColumns";

const MAP_TITLE_DIALOG = {
    role: "Asignación de roles",
    establishment: "Asignación de establecimientos",
    establishmentList: "Listado de establecimientos",
    resetPassword: "Resetear contraseña",
    infoUser: "Información del usuario",
};

const UsersList = ({
    isSuperAdmin,
    getUsersFunction,
    columnVisibilityModel,
    title = "Usuarios del Sistema",
    showComponentInFilterBar = true,
    establishment,
}) => {
    const { auth } = useAuth();
    const [establishmentsAdmin, setEstablishmentsAdmin] = useState([]);
    const [establishmentAdmin, setEstablishmentAdmin] = useState(
        establishment || auth.selectedEstablishment
    );
    const [users, setUsers] = useState([]);
    const [openLoader, setOpenLoader] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [filter, setFilter] = useState("");
    const [extraFilter, setExtraFilter] = useState({
        role_name: getUsersRadioGroupItems(auth.selectedRole?.name)[0].value,
    });
    const [selectedUser, setSelectedUser] = useState(null);

    const [openDialog, setOpenDialog] = useState(false);
    const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
    const [openConfirmationModalText, setConfirmationModalText] = useState(
        "¿Está seguro que desea guardar esta información?"
    );
    const [resetPasswordFirstConfirmation, setResetPasswordFirstConfirmation] = useState(false);
    const [dialogContent, setDialogContent] = useState("");

    const [usersColumns, setUsersColumns] = useState();

    const [showError, setShowError] = useState(false);

    const [showAlert, setShowAlert] = useState(false);
    const [alertText, setAlertText] = useState("");
    const [alertSeverity, setAlertSeverity] = useState("");

    const [newUserPassword, setNewUserPassword] = useState("");
    const [confirmNewUserPassword, setConfirmNewUserPassword] = useState("");

    const [extraFilterComponent, setExtraFilterComponent] = useState({
        establishment:
            showComponentInFilterBar && auth.selectedRole?.name === "superadmin"
                ? ""
                : establishment?.id || auth.selectedEstablishment?.id || "",
    });

    const [roleEstablishment, setRoleEstablishment] = useState({});

    const extraFilterList = [
        {
            type: "radio",
            key: "role_name",
            items: getUsersRadioGroupItems(auth.selectedRole?.name),
        },
    ];

    const openModal = (user) => {
        setSelectedUser(user);
        setOpenDialog(true);
    };

    const openResetPasswordConfirmation = (user) => {
        setSelectedUser(user);
        setResetPasswordFirstConfirmation(true);
    };

    const getUsers = async (filter = "", extraFilter = {}, page = 1, pageSize = 10) => {
        setOpenLoader(true);
        if (extraFilter.role_name === "pending") {
            setUsersColumns(
                getUsersColumns.getUsersColumnsWithPending(setDialogContent, openModal)
            );
        } else {
            setUsersColumns(
                getUsersColumns.getUsersColumnsWithoutPending(
                    setDialogContent,
                    openModal,
                    isSuperAdmin,
                    openResetPasswordConfirmation
                )
            );
        }

        const res = await getUsersFunction(page, pageSize, {
            ...extraFilter,
            search: filter,
            role_name: extraFilter.role_name !== "pending" ? extraFilter.role_name : "",
            roles_pending: extraFilter.role_name === "pending",
        });
        setRowCount(res.data.count);
        setPageSize(pageSize);
        setPage(page);
        setFilter(filter);
        setExtraFilter(extraFilter);
        setUsers(res.data.results);
        setOpenLoader(false);
    };

    const handleSave = async () => {
        try {
            setOpenConfirmationDialog(false);
            let res;
            let alertMessage = "Acción realizada con éxito";
            if (dialogContent === "establishment") {
                const data = {
                    ...roleEstablishment,
                    is_super_admin: isSuperAdmin,
                };
                res = await ApiService.updateUserRoles(selectedUser.id, data);
            } else if (dialogContent === "resetPassword") {
                const dataPassword = {
                    ...newUserPassword,
                    user_id: selectedUser.id,
                };
                res = await ApiService.resetPasswordSa(dataPassword);
                alertMessage = (
                    <p>
                        La contraseña fue reseteada exitosamente, contacte al usuario{" "}
                        <strong>
                            {selectedUser.first_name} {selectedUser.last_name}
                        </strong>{" "}
                        email: <strong>{selectedUser.email}</strong> e infórmele su nueva contraseña
                        para poder ingresar al sistema. Al hacerlo, indíquele que la primera vez al
                        ingresar a la plataforma con esta contraseña, deberá dirigirse a su perfil y
                        cambiar la contraseña asignada por una nueva que tenga un mayor nivel de
                        seguridad.
                    </p>
                );
            }
            if (res.status === 200) {
                setAlertText(alertMessage);
                setAlertSeverity("success");
                setShowAlert(true);
                setResetPasswordFirstConfirmation(false);
                setOpenDialog(false);
                setDialogContent("");
                setTimeout(() => {
                    setShowAlert(false);
                    setAlertText("");
                }, 10000);
            } else {
                setAlertText("Ocurrió un error, intente nuevamente!");
                setAlertSeverity("error");
                setShowAlert(true);
                setTimeout(() => {
                    setShowAlert(false);
                    setAlertText("");
                }, 2500);
            }
        } catch (error) {
            setAlertText("Ocurrió un error, intente nuevamente!");
            setAlertSeverity("error");
            setShowAlert(true);
            setTimeout(() => {
                setShowAlert(false);
                setAlertText("");
            }, 2500);
        }
    };

    const handleOpenConfirmationModal = () => {
        if (dialogContent === "establishment") {
            if (
                selectedUser.id === null ||
                !roleEstablishment.establishment ||
                !roleEstablishment.roles.length > 0
            ) {
                setShowError(true);

                setTimeout(() => {
                    setShowError(false);
                }, 2500);
            } else {
                setOpenConfirmationDialog(true);
            }
        } else if (dialogContent === "resetPassword") {
            if (
                selectedUser.id === null ||
                newUserPassword.password === "" ||
                newUserPassword.password === undefined
            ) {
                setShowAlert(true);
                setAlertSeverity("error");
                setAlertText("Debe ingresar una contraseña");

                setTimeout(() => {
                    setShowAlert(false);
                }, 2500);
            } else if (newUserPassword.password !== confirmNewUserPassword.password) {
                setShowAlert(true);
                setAlertSeverity("error");
                setAlertText("Las contraseñas ingresadas no coinciden");

                setTimeout(() => {
                    setShowAlert(false);
                }, 2500);
            } else if (
                newUserPassword.password.length < 8 ||
                confirmNewUserPassword.password.length < 8 ||
                newUserPassword.password.length > 16 ||
                confirmNewUserPassword.password.length > 16
            ) {
                setAlertText("La contraseña debe entre 8 y 16 caracteres");
                setAlertSeverity("error");

                setShowAlert(true);
                setTimeout(() => {
                    setShowAlert(false);
                    setAlertText("");
                    setAlertSeverity("");
                }, 2500);
                return;
            } else {
                setConfirmationModalText(
                    <div>
                        <p>
                            La acción que usted va a realizar es irreversible y afectará el ingreso
                            a la plataforma para el usuario{" "}
                            <strong>
                                {selectedUser.first_name} {selectedUser.last_name}
                            </strong>{" "}
                            email: <strong>{selectedUser.email}</strong>. De confirmar esta acción
                            usted deberá notificar al usuario vía mail (o el medio que elija) de su
                            nueva contraseña.
                        </p>
                        <p>¿Reconfirma que desea realizarla de todos modos?</p>
                    </div>
                );
                setOpenConfirmationDialog(true);
            }
        }
    };

    const componentFilterBarSuperAdmin = (
        <SelectProvinceAndLocation
            handleChangeProvince={() => {}}
            handleChangeLocation={() => {}}
            handleChangeEstablishment={(establishment) => {
                setExtraFilterComponent({
                    ...extraFilterComponent,
                    establishment: establishment?.id,
                });
            }}
            showEstablishments={true}
            gridSizes={{
                province: { xs: 12, lg: 4, sm: 4 },
                location: { xs: 12, lg: 4, sm: 4 },
                establishment: { xs: 12, lg: 4, sm: 4 },
            }}
        />
    );

    const componentFilterBarAdmin = (
        <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
                <Autocomplete
                    value={establishmentAdmin}
                    id="autocomplete"
                    freeSolo
                    // options={products.map((option) => option.name)}
                    options={establishmentsAdmin}
                    renderOption={(props, option) => {
                        return (
                            <li {...props} key={option.id}>
                                {option.name}
                            </li>
                        );
                    }}
                    getOptionLabel={(option) => {
                        return option.name;
                    }}
                    renderInput={(params) => (
                        <TextField {...params} label="Seleccione una establecimiento" />
                    )}
                    onChange={(e, v) => {
                        setEstablishmentAdmin(v);
                        setExtraFilterComponent({
                            ...extraFilterComponent,
                            establishment: v?.id,
                        });
                    }}
                />
            </Grid>
        </Grid>
    );

    useEffect(() => {
        setUsersColumns(
            getUsersColumns.getUsersColumnsWithoutPending(
                setDialogContent,
                openModal,
                isSuperAdmin,
                setConfirmNewUserPassword
            )
        );
        setEstablishmentsAdmin(auth.rolesEstablishment.map((re) => re.establishment));
    }, []);

    return (
        <div>
            <h1> {title} </h1>
            {usersColumns && (
                <FilterBar
                    labelSearch="Buscar por nombre o email"
                    extraFilterCallback={() => setUsers([])}
                    columnVisibilityModel={columnVisibilityModel}
                    onPageChange={getUsers}
                    openLoader={openLoader}
                    rowCount={rowCount}
                    columns={usersColumns}
                    rows={users}
                    pageSize={pageSize}
                    extraFilterParent={extraFilter}
                    enableExtraFilter
                    extraFilterList={extraFilterList}
                    extraFilterComponent={extraFilterComponent}
                    extraFilterTitle="Usuarios cargados en Alfadatizando"
                    component={
                        showComponentInFilterBar
                            ? isSuperAdmin
                                ? componentFilterBarSuperAdmin
                                : undefined
                            : undefined
                    }
                />
            )}

            {openDialog && (
                <GenericDialog
                    // handleSave={handleSave}
                    handleSave={handleOpenConfirmationModal}
                    open={openDialog}
                    setOpen={setOpenDialog}
                    // cancelButton={dialogContent === "establishment"}
                    saveButton={
                        dialogContent === "establishment" || dialogContent === "resetPassword"
                    }
                    title={
                        !isSuperAdmin && dialogContent === "establishment"
                            ? `${MAP_TITLE_DIALOG["role"]} - ${
                                  establishment?.name || auth.selectedEstablishment?.name || ""
                              }`
                            : MAP_TITLE_DIALOG[dialogContent] || ""
                    }
                    onClose={() => {
                        getUsers(filter, extraFilter, page, pageSize);
                    }}
                    dialogWidth={dialogContent === "role"}
                >
                    {dialogContent === "role" && (
                        <DialogUserRoleContent
                            selectedUser={selectedUser}
                            handleChangeExternalData={(data) => {
                                setRoleEstablishment({
                                    ...roleEstablishment,
                                    ...data,
                                });
                            }}
                        ></DialogUserRoleContent>
                    )}
                    {dialogContent === "establishment" && (
                        <DialogUserEstablishmentContent
                            defaultEstablishment={establishmentAdmin}
                            isSuperAdmin={isSuperAdmin}
                            selectedUser={selectedUser}
                            handleChangeExternalData={(data) => {
                                setRoleEstablishment({
                                    ...roleEstablishment,
                                    ...data,
                                });
                            }}
                            showProvinceAndLocation={isSuperAdmin}
                        ></DialogUserEstablishmentContent>
                    )}
                    {dialogContent === "establishmentList" && (
                        <DialogEstablishmentListComponent
                            defaultEstablishment={establishmentAdmin}
                            isSuperAdmin={isSuperAdmin}
                            selectedUser={selectedUser}
                            handleChangeExternalData={(data) => {
                                setRoleEstablishment({
                                    ...roleEstablishment,
                                    ...data,
                                });
                            }}
                        ></DialogEstablishmentListComponent>
                    )}
                    {dialogContent === "infoUser" && (
                        <DialogUserContent selectedUser={selectedUser} />
                    )}
                    {dialogContent === "resetPassword" && (
                        <DialogResetPasswordContent
                            newPassword={newUserPassword}
                            setNewPassword={setNewUserPassword}
                            confirmPassword={confirmNewUserPassword}
                            setConfirmPassword={setConfirmNewUserPassword}
                        />
                    )}
                </GenericDialog>
            )}

            <Grid
                sx={{
                    position: "fixed",
                    bottom: "40%",
                    left: "35%",
                    zIndex: 9999,
                }}
            >
                {showError && (
                    <GenericAlert
                        severity={"error"}
                        text={"Debe seleccionar todos los campos"}
                        variant="filled"
                    ></GenericAlert>
                )}
            </Grid>

            {resetPasswordFirstConfirmation && (
                <GenericDialog
                    handleSave={() => {
                        setDialogContent("resetPassword");
                        setOpenDialog(true);
                    }}
                    open={resetPasswordFirstConfirmation}
                    setOpen={setResetPasswordFirstConfirmation}
                    title="Confirmación"
                    onClose={() => {
                        setResetPasswordFirstConfirmation(false);
                        setOpenDialog(false);
                    }}
                    cancelButton={true}
                    saveButton={true}
                    saveButtonText="Continuar"
                    mandatoryText={false}
                >
                    <DialogContent>
                        <p>
                            Usted está por resetear la contraseña del usuario{" "}
                            <strong>
                                {selectedUser.first_name} {selectedUser.last_name}
                            </strong>
                            <br />
                            email: <strong>"{selectedUser.email}"</strong>
                        </p>
                        <p>¿Está seguro de querer realizar esta acción?</p>
                    </DialogContent>
                </GenericDialog>
            )}

            {openConfirmationDialog && (
                <GenericDialog
                    handleSave={handleSave}
                    open={openConfirmationDialog}
                    setOpen={setOpenConfirmationDialog}
                    // cancelButton={dialogContent === "establishment"}
                    // saveButton={dialogContent === "establishment"}
                    title={"Confirmación"}
                    onClose={() => {
                        getUsers(filter, extraFilter, page, pageSize);
                    }}
                    mandatoryText={false}
                >
                    <Typography variant="p" px={3}>
                        {openConfirmationModalText}
                    </Typography>
                </GenericDialog>
            )}
            {showAlert && (
                <Alert
                    sx={{
                        zIndex: 99999,
                        position: "fixed",
                        top: "45%",
                        left: "35%",
                        maxWidth: "600px",
                        color: "white",
                        fontWeight: "bold",
                    }}
                    variant="filled"
                    severity={alertSeverity}
                >
                    {alertText}
                </Alert>
            )}
        </div>
    );
};

export default UsersList;
